// packages
import { index } from 'd3-array';
import { json } from 'd3-fetch';
import { derived, writable, get } from 'svelte/store';

// local
import { find } from './utils/array.js';
import {
  LA_COUNTY_URL,
  STATE_URL,
  OC_URL,
  DA_URL,
  PRESIDENT,
  USHOUSE_URL,
  USGOV_URL,
  SENATE_URL,
  TRENDS,
  SD_URL,
} from './constants.js';

/**
 * @template T
 * @typedef {import('svelte/store').Readable<T>} Readable
 */

/**
 * @typedef {import('./types').RaceFeed} RaceFeed
 * @typedef {import('./types').RaceInterface} RaceInterface
 */

/**
 * @typedef {import('./types').DelFeed} DelFeed
 * @typedef {import('./types').DelegatesInterface} DelegatesInterface
 */

/**
 * @param {{id: string}} d
 * @returns {string}
 */
const indexById = (d) => d.id;

/**
 * @param {{ap_id: string}} d
 * @returns {string}
 */
export const indexByApId = (d) => d.ap_id;

/**
 * @param {{code: string}} d
 * @returns {string}
 */
const indexByCode = (d) => d.code;

/**
 * @param {string} url The URL to request
 * @param {number} seconds How many seconds to wait between new requests
 * @returns {Promise<Readable<RaceFeed>>}
 */
export async function createFeed(url, seconds = 30) {
  // our timeout interval
  const interval = seconds * 1000;

  /**
   * We need to track the active timeout.
   * @type {number | undefined}
   */
  let timeoutId;

  /**
   * We need to keep track of the last run.
   * @type {number}
   */
  let lastUpdated;

  // set up our request function
  async function makeRequest() {
    console.debug('Making request:');

    // NOTED OUT TO USE LOCAL FEED
    const feedData = await json(url);
    let feed = /** @type {RaceFeed} */ (feedData);

    lastUpdated = Date.now();

    // for testing locally
    // comment out for live or ap testing
    // if (url == STATE_URL) {
    //   return stateTestFeed;
    // } else if (url == PRESIDENT) {
    //   return presidentTestFeed;
    // } else if (url == USHOUSE_URL) {
    //   return houseTestFeed;
    // } else if (url == USGOV_URL) {
    //   return govTestFeed;
    // } else if (url == SENATE_URL) {
    //   return senateTestFeed;
    //   // } else if (url == DA_URL) {
    //   //   // return daTestFeed;
    //   // }
    // } else if (url == TRENDS) {
    //   return trendsTestFeed;
    // } else {
    //   // return feed;
    //   return feed;
    // }

    // uncomment out for ap testing
    // comment out to use local test feeds
    return feed;
  }

  // get the initial data
  const feed = await makeRequest();

  // create our store
  const { subscribe } = writable(feed, function start(set) {
    async function run() {
      // make our request
      const feed = await makeRequest();

      // pass it along
      console.debug('Making update:');
      set(feed);

      timeoutId = window.setTimeout(run, interval);
    }

    const now = Date.now();
    // turn this on again for live
    // starting here
    if (now - lastUpdated < interval) {
      // we just had a run, so cool it
      timeoutId = window.setTimeout(run, interval);
    } else {
      // it's been a while, so get going!
      run();
    }
    // ending here

    return function stop() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
  });

  return { subscribe };
}

/**
 * @param {Readable<RaceFeed>} store
 * @param {string} office
 */
export function createOfficeFeed(store, office) {
  return derived(store, ($feed) => {
    const feed = [];

    for (let idx = 0; idx < $feed.length; idx++) {
      const item = $feed[idx];

      // if (item.office === office && item.race_type != 'G') {
      //   feed.push(item);
      // }
      if (item.office === office) {
        feed.push(item);
      }
    }
    const sorted = feed.sort((a, b) => a.seat_number - b.seat_number);
    return sorted;
    // return feed;
  });
}

/**
 * @param {Readable<RaceFeed>} store
 * @param {string} raceId
 * @returns {Readable<RaceFeed>}
 */
export function createRaceFeed(store, raceId) {
  return derived(store, ($feed) => find($feed, (race) => race.id === raceId));
}

/**
 * @param {Readable<RaceInterface>} store
 * @param {string} candidateId
 */
export function createCandidateFeed(store, candidateId) {
  return derived(store, ($feed) =>
    find($feed.candidates, (candidate) => candidate.ap_id === candidateId)
  );
}

/**
 * @param {Readable<RaceFeed>} store
 */
export function createRaceLookup(store) {
  return derived(store, ($feed) => index($feed, indexById));
}

/**
 * @param {Readable<RaceInterface>} store
 */
export function createCandidateLookup(store) {
  return derived(store, ($race) => index($race.candidates, indexByApId));
}

/**
 * @param {Readable<RaceInterface>} store
 */
export function createSubunitLookup(store) {
  return derived(store, ($race) => index($race.subunits, indexByCode));
}

/**
 * @param {Readable<import('./types').RaceLookup>} store
 * @param {string} raceId
 */

export function createRaceStore(store, raceId) {
  return derived(store, ($lookup) => $lookup.get(raceId));
}
